import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import '../site-v3/legacy-v3.css'; // Barlow, the trasseapp.com face (2026-10-28)

/**
 * Public one-time page: /trip/reveal?token=…
 * Fetches credentials from pear-payments (no Firebase Auth). Link expires / one-shot.
 */
const BASE = (process.env.REACT_APP_PAYMENTS_URL || "").replace(/\/$/, "");

type Creds = { username: string; password: string; displayName: string };

const TripConductorReveal: React.FC = () => {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [creds, setCreds] = useState<Creds | null>(null);

  useEffect(() => {
    let live = true;
    (async () => {
      if (!token) {
        setLoading(false);
        setError("Missing link token.");
        return;
      }
      if (!BASE) {
        setLoading(false);
        setError("Payments backend URL is not configured.");
        return;
      }
      try {
        const res = await fetch(`${BASE}/api/trip/conductor-reveal/${encodeURIComponent(token)}`);
        const json = await res.json().catch(() => ({}));
        if (!live) return;
        if (!res.ok || json.ok === false) {
          setError(json.error || "This link is invalid or already used.");
        } else {
          setCreds({
            username: json.username,
            password: json.password,
            displayName: json.displayName || "Trip conductor",
          });
        }
      } catch {
        if (live) setError("Could not reach the server. Try again on a better connection.");
      } finally {
        if (live) setLoading(false);
      }
    })();
    return () => { live = false; };
  }, [token]);

  const copy = (t: string) => {
    try { navigator.clipboard?.writeText(t); } catch { /* ignore */ }
  };

  const card: React.CSSProperties = {
    maxWidth: 440, margin: "8vh auto", padding: 28, borderRadius: 14,
    boxShadow: "0 8px 30px rgba(0,0,0,0.08)", background: "#fff",
    fontFamily: "Barlow, -apple-system, Segoe UI, Roboto, sans-serif", color: "#0F1419",
  };

  if (loading) return <div style={card}>Opening your one-time login…</div>;

  if (error || !creds) {
    return (
      <div style={card}>
        <div style={{ fontWeight: 800, fontSize: 18, color: "#00707F", marginBottom: 10 }}>trasse</div>
        <h2 style={{ fontSize: 18, margin: "0 0 8px" }}>Link not available</h2>
        <p style={{ fontSize: 14, color: "#2A3138", lineHeight: 1.55 }}>
          {error || "This one-time link is invalid, already used, or expired."}
        </p>
        <p style={{ fontSize: 13, color: "#5D646B", marginTop: 12 }}>
          Fleet drivers can still open the login in the Trasse driver app. Hired crew should ask the school admin to resend.
        </p>
      </div>
    );
  }

  return (
    <div style={card}>
      <div style={{ fontWeight: 800, fontSize: 18, color: "#00707F", marginBottom: 10 }}>trasse</div>
      <h2 style={{ fontSize: 18, margin: "0 0 6px" }}>Conductor login</h2>
      <p style={{ fontSize: 13.5, color: "#5D646B", lineHeight: 1.5, margin: "0 0 16px" }}>
        Hand these credentials to <b>{creds.displayName}</b>. This page only works once — copy now.
      </p>
      <div style={{ background: "#E2F5F8", borderRadius: 12, padding: 14, marginBottom: 10 }}>
        <div style={{ fontSize: 11, fontWeight: 700, color: "#8C9196", letterSpacing: 0.4 }}>LOGIN / USERNAME</div>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 8 }}>
          <code style={{ fontSize: 15 }}>{creds.username}</code>
          <button type="button" onClick={() => copy(creds.username)} style={{ border: "none", background: "#00707F", color: "#fff", borderRadius: 8, padding: "6px 10px", fontWeight: 600, cursor: "pointer" }}>Copy</button>
        </div>
      </div>
      <div style={{ background: "#FBEFD9", borderRadius: 12, padding: 14 }}>
        <div style={{ fontSize: 11, fontWeight: 700, color: "#8C9196", letterSpacing: 0.4 }}>PASSWORD</div>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 8 }}>
          <code style={{ fontSize: 15 }}>{creds.password}</code>
          <button type="button" onClick={() => copy(creds.password)} style={{ border: "none", background: "#00707F", color: "#fff", borderRadius: 8, padding: "6px 10px", fontWeight: 600, cursor: "pointer" }}>Copy</button>
        </div>
      </div>
      <p style={{ fontSize: 12.5, color: "#8C9196", marginTop: 16, lineHeight: 1.45 }}>
        Sign in on the Trasse app with username + password (not email). Closing this tab won’t show the password again.
      </p>
    </div>
  );
};

export default TripConductorReveal;
